import React, { useState } from 'react';
import { MapPinIcon, ChevronDownIcon, ClockIcon, PhoneIcon, FuelIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import { GasStation } from '../data/stations';
interface StationSelectorProps {
  stations: GasStation[];
  selectedStation: GasStation | null;
  onSelectStation: (station: GasStation) => void;
}
export function StationSelector({
  stations,
  selectedStation,
  onSelectStation
}: StationSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="relative" data-id="element-813">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-3 bg-white border-2 border-gray-200 hover:border-blue-500 px-4 py-3 rounded-xl transition-all shadow-sm hover:shadow-md" data-id="element-814">
        <MapPinIcon className="w-5 h-5 text-blue-600" data-id="element-815" />
        <div className="text-left" data-id="element-816">
          <p className="text-xs text-gray-500 font-medium" data-id="element-817">Station Service</p>
          <p className="text-sm font-bold text-gray-900" data-id="element-818">
            {selectedStation ? selectedStation.name : 'Sélectionner une station'}
          </p>
        </div>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-819" />
      </button>
      {isOpen && <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} data-id="element-820" />
          <div className="absolute top-full left-0 mt-2 w-[500px] bg-white border-2 border-gray-200 rounded-xl shadow-2xl z-20 max-h-[600px] overflow-y-auto" data-id="element-821">
            <div className="p-2" data-id="element-822">
              {stations.map(station => <button key={station.id} onClick={() => {
            onSelectStation(station);
            setIsOpen(false);
          }} className={`w-full text-left p-4 rounded-lg transition-colors ${selectedStation?.id === station.id ? 'bg-blue-50 border-2 border-blue-500' : 'hover:bg-gray-50 border-2 border-transparent'}`} data-id="element-823">
                  <div className="flex items-start justify-between mb-3" data-id="element-824">
                    <div className="flex-1" data-id="element-825">
                      <div className="flex items-center gap-2 mb-1" data-id="element-826">
                        <p className="font-bold text-gray-900" data-id="element-827">
                          {station.name}
                        </p>
                        <span className="px-2 py-1 bg-blue-600 text-white text-xs font-bold rounded" data-id="element-828">
                          {station.code}
                        </span>
                        {station.isOperational ? <CheckCircleIcon className="w-4 h-4 text-green-600" data-id="element-829" /> : <AlertCircleIcon className="w-4 h-4 text-red-600" data-id="element-830" />}
                      </div>
                      <p className="text-sm text-gray-600" data-id="element-831">
                        {station.location}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-832">
                    <div className="text-xs bg-gray-100 px-2 py-1 rounded-md text-gray-700" data-id="element-833">
                      {station.commune}
                    </div>
                    <div className={`text-xs px-2 py-1 rounded-md font-semibold ${station.riverSide === 'RG' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-834">
                      {station.riverSide === 'RG' ? 'Rive Gauche' : 'Rive Droite'}
                    </div>
                    <div className="text-xs bg-gray-100 px-2 py-1 rounded-md text-gray-700" data-id="element-835">
                      {station.pumps} pompes
                    </div>
                    <div className="text-xs bg-gray-100 px-2 py-1 rounded-md text-gray-700" data-id="element-836">
                      Cap: {(station.capacity / 1000).toFixed(0)}k L
                    </div>
                    <div className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded-md text-gray-700" data-id="element-837">
                      <ClockIcon className="w-3 h-3" data-id="element-838" />
                      {station.openingHours}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-2 text-xs" data-id="element-839">
                    <PhoneIcon className="w-3 h-3 text-gray-500" data-id="element-840" />
                    <span className="text-gray-600" data-id="element-841">
                      {station.managerContact}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-2" data-id="element-842">
                    <div className="flex items-center gap-1" data-id="element-843">
                      <FuelIcon className="w-3 h-3 text-blue-600" data-id="element-844" />
                      <span className="text-xs font-semibold text-blue-900" data-id="element-845">
                        {station.prices.essence} FCFA/L
                      </span>
                      {!station.fuelAvailability.essence && <span className="text-xs text-red-600" data-id="element-846">
                          (Indisponible)
                        </span>}
                    </div>
                    <div className="flex items-center gap-1" data-id="element-847">
                      <FuelIcon className="w-3 h-3 text-orange-600" data-id="element-848" />
                      <span className="text-xs font-semibold text-orange-900" data-id="element-849">
                        {station.prices.diesel} FCFA/L
                      </span>
                      {!station.fuelAvailability.diesel && <span className="text-xs text-red-600" data-id="element-850">
                          (Indisponible)
                        </span>}
                    </div>
                    {station.prices.gpl && <div className="flex items-center gap-1" data-id="element-851">
                        <FuelIcon className="w-3 h-3 text-purple-600" data-id="element-852" />
                        <span className="text-xs font-semibold text-purple-900" data-id="element-853">
                          {station.prices.gpl} FCFA/L
                        </span>
                        {!station.fuelAvailability.gpl && <span className="text-xs text-red-600" data-id="element-854">
                            (Indisponible)
                          </span>}
                      </div>}
                  </div>
                  {!station.isOperational && <div className="bg-red-50 border border-red-200 rounded-md px-2 py-1 mt-2" data-id="element-855">
                      <p className="text-xs text-red-700 font-semibold" data-id="element-856">
                        Station Hors Service
                      </p>
                    </div>}
                </button>)}
            </div>
          </div>
        </>}
    </div>;
}